// BSG: product reviews — Judge.me wrapper.
//
// Judge.me renders its widget client-side from JSON, so at page load
// .candells-reviews__widget holds nothing but an empty placeholder div. This file
// watches the stage and reports what Judge.me eventually paints back onto our root as
// data-reviews-state, which is the single hook the CSS keys off:
//
//   loading    nothing readable yet (ships in the markup; our CTAs are disabled)
//   empty      Judge.me reported 0 reviews  -> our empty panel replaces its own
//   populated  Judge.me rendered reviews    -> its markup is shown, restyled
//   unknown    nothing readable after SETTLE_MS (blocked script, app block removed,
//              widget error) -> whatever Judge.me did render is left completely alone,
//              so the section degrades to a plain widget rather than to a blank band.
//
// It never writes review data: counts and averages are only ever mirrored from
// Judge.me's own attributes, never computed, guessed or invented here.
//
// WHICH WIDGET. Captured live on 2026-09-17: this shop serves Judge.me's REVAMPED
// widget (root .jdgm-widget-revamp, internals `jm-*`, Vue). It publishes no review
// count or average attribute, so the state is read from the nodes it renders instead:
// a review item means populated, its own no-reviews block means empty. The LEGACY v3
// widget (.jdgm-rev-widg[data-number-of-reviews]) is still in Judge.me's bundle and is
// checked first, because when it is present it gives us the real total — which is the
// only thing that can legitimately set data-reviews-count.
//
// Loaded with defer by sections/candells-product-reviews.liquid.
(function () {
  'use strict';

  // Judge.me's script comes from its app embed and can be slow on a cold cache; 8s is
  // long enough that a normal render never trips the fallback.
  var SETTLE_MS = 8000;
  // Its review form opens a repaint or several after the click.
  var FORM_TRIES = 20;
  var FORM_INTERVAL_MS = 100;

  // Revamp first, then legacy. Order matters only for the write link.
  var WRITE_SELECTOR = [
    '[data-testid="write-review-button"]',
    '.jm-action-buttons__button',
    '.jdgm-write-rev-link',
  ].join(', ');

  var REVIEW_SELECTOR = [
    '.jm-review-item',
    '.jm-align-review-item',
    '.jm-carousel-review-item',
    '.jdgm-rev',
  ].join(', ');

  var NO_REVIEWS_SELECTOR = [
    '.jm-no-reviews-state',
    '.jdgm-empty-state',
    '.jdgm-rev-widg__body--empty',
  ].join(', ');

  function setUp(root) {
    var stage = root.querySelector('[data-candells-reviews-stage]');
    if (!stage) return;

    var panel = stage.querySelector('[data-candells-reviews-empty]');
    var settled = false;

    function setState(state) {
      if (root.getAttribute('data-reviews-state') === state) return;
      root.setAttribute('data-reviews-state', state);
      if (panel) panel.hidden = state !== 'empty';
      var list = root.querySelectorAll('[data-candells-write]');
      for (var i = 0; i < list.length; i++) list[i].disabled = state === 'loading';
    }

    // Judge.me's wording for its own write link is set in its admin; the section
    // setting wins so the CTAs on the page cannot disagree. Text-only nodes only —
    // never reach into a control that has element children of its own.
    function syncWriteLabel() {
      var label = root.getAttribute('data-write-label');
      if (!label) return;
      var link = stage.querySelector(WRITE_SELECTOR);
      if (!link || link.children.length) return;
      if (link.textContent.trim() !== label) link.textContent = label;
    }

    // Only the legacy widget publishes an average. The numeral is drawn by CSS from
    // this attribute so it can be set in Pirata One at display size without touching
    // Judge.me's own text node. The revamp widget renders its own numeral, which the
    // CSS scales in place instead.
    function mirrorAverage(widget) {
      var target = stage.querySelector('.jdgm-rev-widg__summary-average') ||
        stage.querySelector('.jdgm-rev-widg__summary');
      if (!target) return;
      var value = parseFloat(widget.getAttribute('data-average-rating'));
      if (isNaN(value) || value <= 0) {
        target.removeAttribute('data-candells-average');
        return;
      }
      target.setAttribute('data-candells-average', value.toFixed(1));
    }

    function read() {
      // 1. Legacy widget: a real total, so the count-dependent CSS can work.
      var legacy = stage.querySelector('.jdgm-rev-widg[data-number-of-reviews]');
      if (legacy) {
        var count = parseInt(legacy.getAttribute('data-number-of-reviews'), 10);
        if (!isNaN(count)) {
          settled = true;
          root.setAttribute('data-reviews-count', String(count));
          mirrorAverage(legacy);
          syncWriteLabel();
          setState(count > 0 ? 'populated' : 'empty');
          return;
        }
      }

      // 2. Revamp widget: judged by what it rendered. No total is published, so
      //    data-reviews-count is deliberately left unset rather than filled with the
      //    number of rows on this page — that is a page size, not a review count.
      var rendered = stage.querySelectorAll(REVIEW_SELECTOR).length;
      if (rendered > 0) {
        settled = true;
        root.setAttribute('data-reviews-shown', String(rendered));
        syncWriteLabel();
        setState('populated');
        return;
      }

      if (stage.querySelector(NO_REVIEWS_SELECTOR)) {
        settled = true;
        root.removeAttribute('data-reviews-shown');
        root.setAttribute('data-reviews-count', '0');
        syncWriteLabel();
        setState('empty');
      }
    }

    // Deliberately never disconnected: Judge.me re-renders the widget on sort, on
    // filter and on every pagination click, which would otherwise strip our write
    // label and leave the state stale.
    var observer = new MutationObserver(read);
    observer.observe(stage, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-number-of-reviews', 'data-average-rating', 'class'],
    });

    window.setTimeout(function () {
      if (!settled) setState('unknown');
    }, SETTLE_MS);

    read();
  }

  /* ── Proxy CTAs ─────────────────────────────────────────────── */

  // Our buttons never build a form of their own — they press Judge.me's own write-review
  // control, so collection, moderation, verified badges and photo uploads keep working.
  // In the empty state that control is inside the block our CSS hides; a programmatic
  // click still fires its listener, which is why it is hidden rather than removed.
  function openForm(root, wantsPhoto) {
    var stage = root.querySelector('[data-candells-reviews-stage]');
    if (!stage) return;

    var link = stage.querySelector(WRITE_SELECTOR);
    if (!link) return;
    link.click();

    // The revamp widget answers with a modal dialog that positions itself, so there is
    // nothing to scroll to. Only the legacy inline form needs bringing into view.
    var tries = 0;
    var timer = window.setInterval(function () {
      tries++;
      var form = stage.querySelector('.jdgm-form-wrapper');
      if (form && form.offsetParent !== null) {
        window.clearInterval(timer);
        form.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (wantsPhoto) {
          var file = form.querySelector('input[type="file"]');
          if (file && file.focus) file.focus();
        }
        return;
      }
      if (tries >= FORM_TRIES) window.clearInterval(timer);
    }, FORM_INTERVAL_MS);
  }

  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('[data-candells-write]');
    if (!button || button.disabled) return;

    var root = button.closest('[data-candells-section="product-reviews"]');
    if (!root) return;

    event.preventDefault();
    openForm(root, button.hasAttribute('data-candells-photo'));
  }

  function init(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var roots = scope.querySelectorAll('[data-candells-section="product-reviews"]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-candells-reviews-ready')) continue;
      roots[i].setAttribute('data-candells-reviews-ready', 'true');
      setUp(roots[i]);
    }
  }

  // Registered exactly once: the theme editor can re-insert this section, and with it
  // this script tag, which would otherwise proxy every CTA press twice.
  if (!document.documentElement.hasAttribute('data-candells-reviews-bound')) {
    document.documentElement.setAttribute('data-candells-reviews-bound', 'true');
    document.addEventListener('click', onClick);
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }

  init(document);
})();
